import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Activity, MessageSquare, Phone, Users, Clock, ArrowUpDown, Filter,
  Sparkles, BarChart3, Settings2, CheckCircle2, Gauge,
} from 'lucide-react';
import { WorkloadChip, WORKLOAD_STYLE, WORKLOAD_DISCLAIMER } from './people/CapacityChip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_COLS = [
  { key: 'manager',              label: 'Manager',        type: 'string', align: 'left' },
  { key: 'teamLeader',           label: 'Team Leader',    type: 'string', align: 'left' },
  { key: 'planCount',            label: 'Plans',          type: 'number', align: 'right' },
  { key: 'totalLots',            label: 'Lots',           type: 'number', align: 'right' },
  { key: 'messagesReceived',     label: 'Emails',         type: 'number', align: 'right' },
  { key: 'meetingsAttended',     label: 'Meetings',       type: 'number', align: 'right' },
  { key: 'callsTotal',           label: 'Calls',          type: 'number', align: 'right' },
  { key: 'talkTimeMinutes',      label: 'Talk (min)',     type: 'number', align: 'right' },
  { key: 'totalHoursWeekly',     label: 'Tracked Hrs/Wk', type: 'number', align: 'right' },
  { key: 'trackedVsTeamAvgPct',  label: 'vs Team Avg',    type: 'number', align: 'right' },
  { key: 'workloadBandKey',      label: 'Tracked Load',   type: 'string', align: 'left'  },
  { key: 'pulseComposite',       label: 'Everperform',    type: 'number', align: 'left'  },
  { key: 'workloadIndex',        label: 'Workload Index', type: 'number', align: 'right' },
];

const DIMENSIONS = [
  { key: 'messagesReceived', label: 'Emails',    color: 'bg-blue-500',    icon: MessageSquare },
  { key: 'meetingsAttended', label: 'Meetings',  color: 'bg-violet-500',  icon: Users },
  { key: 'callsTotal',       label: 'Calls',     color: 'bg-emerald-500', icon: Phone },
  { key: 'talkTimeMinutes',  label: 'Talk time', color: 'bg-amber-500',   icon: Clock },
];

const WorkloadAnalysis = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [annualize, setAnnualize] = useState(false);
  const [teamFilter, setTeamFilter] = useState('all');
  const [sortKey, setSortKey] = useState('workloadIndex');
  const [sortDir, setSortDir] = useState('desc');
  const [chartMetric, setChartMetric] = useState('workloadIndex');
  const [periodDays, setPeriodDays] = useState(31);
  const [periodDraft, setPeriodDraft] = useState('31');
  const [savingPeriod, setSavingPeriod] = useState(false);
  // Capacity settings — live-tunable minutes-per-meeting and weekly baseline.
  const [capacitySettings, setCapacitySettings] = useState({ minutesPerMeeting: 90, weeklyHoursBaseline: 37.5 });
  const [meetingDraft, setMeetingDraft] = useState('90');
  const [baselineDraft, setBaselineDraft] = useState('37.5');
  const [savingCapacity, setSavingCapacity] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/workload-analysis`, { params: { annualize } });
      setRows(res.data.rows);
      setMeta(res.data.meta);
      setPeriodDays(res.data.meta.periodDays);
      setPeriodDraft(String(res.data.meta.periodDays));
      const cs = res.data.meta.capacitySettings || { minutesPerMeeting: 90, weeklyHoursBaseline: 37.5 };
      setCapacitySettings(cs);
      setMeetingDraft(String(cs.minutesPerMeeting));
      setBaselineDraft(String(cs.weeklyHoursBaseline));
    } finally {
      setLoading(false);
    }
  }, [annualize]);

  useEffect(() => { load(); }, [load]);

  const savePeriod = async () => {
    const val = parseInt(periodDraft, 10);
    if (!val || val < 1 || val > 730) return;
    setSavingPeriod(true);
    try {
      await axios.post(`${API}/calls-meta`, { periodDays: val });
      await load();
    } finally {
      setSavingPeriod(false);
    }
  };

  const saveCapacity = async () => {
    const minutes = parseInt(meetingDraft, 10);
    const baseline = parseFloat(baselineDraft);
    if (!minutes || minutes < 1 || minutes > 600) return;
    if (!baseline || baseline <= 0 || baseline > 168) return;
    setSavingCapacity(true);
    try {
      await axios.post(`${API}/capacity-settings`, {
        minutesPerMeeting: minutes,
        weeklyHoursBaseline: baseline,
      });
      await load();
    } finally {
      setSavingCapacity(false);
    }
  };

  const teamLeaders = useMemo(() => {
    const set = new Set();
    rows.forEach(r => r.teamLeader && set.add(r.teamLeader));
    return ['all', ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const list = teamFilter === 'all' ? rows : rows.filter(r => r.teamLeader === teamFilter);
    return [...list].sort((a, b) => {
      let av, bv;
      if (sortKey === 'pulseComposite') {
        av = a.pulse?.compositeScore;
        bv = b.pulse?.compositeScore;
      } else {
        av = a[sortKey];
        bv = b[sortKey];
      }
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [rows, teamFilter, sortKey, sortDir]);

  const chartMax = useMemo(() => Math.max(...filtered.map(r => r[chartMetric] || 0), 1), [filtered, chartMetric]);

  const toggleSort = (k) => {
    if (k === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="workload-analysis-loading">
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="workload-analysis" className="space-y-6">
      {/* Hero — matches the WDA / rest-of-app light card treatment */}
      <div
        className="relative overflow-hidden rounded-[10px] p-6 bg-white border border-[#E5E5E5]"
        style={{ boxShadow: 'var(--card-shadow)' }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#171717] flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A3A3A3] mb-1">Cross-team comparison</p>
              <h1 className="text-xl font-bold text-[#171717] font-heading tracking-tight">Workload Analysis</h1>
              <p className="text-sm text-[#737373] mt-1 max-w-2xl leading-relaxed">
                Compare every manager&apos;s workload across emails, calls, meetings and talk-time. Annualise to project the July call data across a full year.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="annualize-toggle"
              onClick={() => setAnnualize(v => !v)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${annualize ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600' : 'bg-[#F5F5F5] border-[#E5E5E5] text-[#525252] hover:border-[#171717]'}`}
            >
              <Sparkles className="w-3 h-3" strokeWidth={2} />
              {annualize ? 'Annualised (×' + (meta?.multiplier || 1) + ')' : 'Actual (uploaded period)'}
            </button>
          </div>
        </div>

        {/* Period-days configurator */}
        <div className="mt-5 pt-4 border-t border-[#E5E5E5] flex items-center gap-3 flex-wrap">
          <Settings2 className="w-3.5 h-3.5 text-[#A3A3A3]" strokeWidth={1.5} />
          <p className="text-[11px] text-[#737373]">Uploaded phone data covers</p>
          <input
            data-testid="period-days-input"
            type="number"
            min="1"
            max="730"
            value={periodDraft}
            onChange={(e) => setPeriodDraft(e.target.value)}
            className="w-20 px-2 py-1 rounded-[6px] bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-sm text-center focus:outline-none focus:border-[#171717]"
          />
          <p className="text-[11px] text-[#737373]">days</p>
          {parseInt(periodDraft, 10) !== periodDays && (
            <button
              data-testid="save-period-btn"
              disabled={savingPeriod}
              onClick={savePeriod}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              <CheckCircle2 className="w-3 h-3" /> Save
            </button>
          )}
          <p className="text-[10px] text-[#A3A3A3] ml-auto">{meta?.note}</p>
        </div>

        {/* Capacity vs 37.5h baseline — live-tunable */}
        <div className="mt-3 pt-3 border-t border-[#E5E5E5] flex items-center gap-3 flex-wrap">
          <Gauge className="w-3.5 h-3.5 text-[#A3A3A3]" strokeWidth={1.5} />
          <p className="text-[11px] text-[#737373]">Capacity check assumes</p>
          <input
            data-testid="minutes-per-meeting-input"
            type="number"
            min="1"
            max="600"
            value={meetingDraft}
            onChange={(e) => setMeetingDraft(e.target.value)}
            className="w-20 px-2 py-1 rounded-[6px] bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-sm text-center focus:outline-none focus:border-[#171717]"
          />
          <p className="text-[11px] text-[#737373]">mins per meeting · uses exact talk-time / handle-time for calls &amp; emails · reference baseline</p>
          <input
            data-testid="weekly-baseline-input"
            type="number"
            min="1"
            max="168"
            step="0.5"
            value={baselineDraft}
            onChange={(e) => setBaselineDraft(e.target.value)}
            className="w-20 px-2 py-1 rounded-[6px] bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-sm text-center focus:outline-none focus:border-[#171717]"
          />
          <p className="text-[11px] text-[#737373]">hrs / week (reference only)</p>
          {(parseInt(meetingDraft, 10) !== capacitySettings.minutesPerMeeting
              || parseFloat(baselineDraft) !== capacitySettings.weeklyHoursBaseline) && (
            <button
              data-testid="save-capacity-btn"
              disabled={savingCapacity}
              onClick={saveCapacity}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              <CheckCircle2 className="w-3 h-3" /> Save
            </button>
          )}
        </div>

        {/* Disclaimer + Team-average context */}
        <div
          data-testid="capacity-disclaimer"
          className="mt-3 pt-3 border-t border-[#E5E5E5] text-[11px] text-[#525252] leading-relaxed"
        >
          <span className="font-semibold text-amber-700">Tracked activities only.</span>{' '}
          {WORKLOAD_DISCLAIMER.replace('Tracked activities only — ', '')}
          {meta?.teamAvgHoursWeekly != null && (
            <>
              {' '}The team currently averages{' '}
              <span className="font-bold text-[#171717]">{meta.teamAvgHoursWeekly.toFixed(1)} tracked hrs/week</span>
              {meta.teamAvgPctOfBaseline != null && (
                <> — roughly {Math.round(meta.teamAvgPctOfBaseline)}% of the {capacitySettings.weeklyHoursBaseline}h reference.</>
              )}
              {' '}Rows are labelled <em>relative to this team average</em>, not to a full workweek.
            </>
          )}
        </div>

        {/* Band summary */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="capacity-summary">
          {['peak', 'elevated', 'typical', 'light'].map(k => {
            const style = WORKLOAD_STYLE[k];
            const count = meta?.workloadBandCounts?.[k] ?? 0;
            return (
              <div key={k} className={`px-3 py-1.5 rounded-[8px] border ${style.border} bg-white/95`}>
                <p className="text-[9px] uppercase tracking-wider font-semibold text-[#737373]">{style.label}</p>
                <p className={`text-lg font-bold ${style.text} font-heading`}>{count}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters + Chart controls */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-3 h-3 text-[#A3A3A3]" strokeWidth={2} />
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#737373]">Team</span>
          <select
            data-testid="team-filter"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="px-2 py-1.5 text-xs rounded-[6px] border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#171717]"
          >
            {teamLeaders.map(tl => (
              <option key={tl} value={tl}>{tl === 'all' ? 'All Teams' : `Team: ${tl}`}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <BarChart3 className="w-3 h-3 text-[#A3A3A3]" strokeWidth={2} />
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#737373]">Chart</span>
          <select
            data-testid="chart-metric"
            value={chartMetric}
            onChange={(e) => setChartMetric(e.target.value)}
            className="px-2 py-1.5 text-xs rounded-[6px] border border-[#E5E5E5] bg-white focus:outline-none focus:border-[#171717]"
          >
            <option value="workloadIndex">Workload Index</option>
            <option value="totalHoursWeekly">Tracked Hrs / Week</option>
            <option value="trackedVsTeamAvgPct">vs Team Avg %</option>
            <option value="messagesReceived">Emails</option>
            <option value="meetingsAttended">Meetings</option>
            <option value="callsTotal">Calls</option>
            <option value="talkTimeMinutes">Talk time (min)</option>
          </select>
        </div>
        <p className="text-[10px] text-[#A3A3A3] ml-auto">{filtered.length} of {rows.length} rows</p>
      </div>

      {/* Bar chart */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-[#171717]">
            {SORT_COLS.find(c => c.key === chartMetric)?.label} · Comparison
          </h3>
          <p className="text-[10px] text-[#A3A3A3]">Sorted by selected metric</p>
        </div>
        <div className="space-y-1.5" data-testid="workload-chart">
          {[...filtered].sort((a, b) => (b[chartMetric] || 0) - (a[chartMetric] || 0)).map(r => {
            const val = r[chartMetric] || 0;
            const pct = chartMax > 0 ? (val / chartMax) * 100 : 0;
            return (
              <div key={r.manager} className="flex items-center gap-3">
                <div className="w-40 flex-shrink-0">
                  <p className="text-[12px] font-medium text-[#171717] truncate">{r.manager}</p>
                  <p className="text-[9px] text-[#A3A3A3] truncate">{r.teamLeader || '—'}</p>
                </div>
                <div className="flex-1 h-6 bg-[#F5F5F5] rounded-[4px] overflow-hidden relative">
                  <div
                    className="h-full rounded-[4px] bg-gradient-to-r from-[#171717] to-[#404040] transition-all"
                    style={{ width: `${pct}%` }}
                  />
                  <p className="absolute inset-0 flex items-center px-2 text-[11px] font-semibold text-[#171717]" style={{ left: pct > 20 ? undefined : `${pct + 1}%` }}>
                    <span className={pct > 20 ? 'text-white ml-1' : 'text-[#171717]'}>
                      {typeof val === 'number' ? val.toLocaleString() : val}
                    </span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Composition stacked bars — proportional makeup of each manager's workload */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-[#171717]">Workload Composition</h3>
          <div className="flex items-center gap-3">
            {DIMENSIONS.map(d => (
              <div key={d.key} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded ${d.color}`} />
                <span className="text-[10px] text-[#525252] uppercase tracking-wider">{d.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2" data-testid="workload-composition">
          {filtered.map(r => {
            const parts = DIMENSIONS.map(d => ({ ...d, val: r[d.key] || 0 }));
            const total = parts.reduce((s, p) => s + p.val, 0) || 1;
            return (
              <div key={r.manager} className="flex items-center gap-3">
                <div className="w-40 flex-shrink-0">
                  <p className="text-[12px] font-medium text-[#171717] truncate">{r.manager}</p>
                </div>
                <div className="flex-1 h-5 rounded-[4px] overflow-hidden flex bg-[#F5F5F5]">
                  {parts.map(p => (
                    <div key={p.key} className={p.color} style={{ width: `${(p.val / total) * 100}%` }} title={`${p.label}: ${p.val.toLocaleString()}`} />
                  ))}
                </div>
                <p className="w-16 text-right text-[11px] text-[#525252] font-mono">{total.toLocaleString()}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] overflow-x-auto">
        <table className="w-full" data-testid="workload-table">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
            <tr>
              {SORT_COLS.map(col => (
                <th key={col.key} className={`py-3 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#737373] whitespace-nowrap text-${col.align}`}>
                  <button onClick={() => toggleSort(col.key)} className="inline-flex items-center gap-1 hover:text-[#171717]">
                    {col.label}
                    <ArrowUpDown className={`w-2.5 h-2.5 ${sortKey === col.key ? 'text-[#171717]' : 'text-[#D4D4D4]'}`} />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.manager} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA]/40">
                <td className="py-2 px-3 text-sm font-medium text-[#171717]">{r.manager}</td>
                <td className="py-2 px-3 text-[11px] text-[#737373]">{r.teamLeader || '—'}</td>
                <td className="py-2 px-3 text-sm text-right text-[#525252]">{r.planCount}</td>
                <td className="py-2 px-3 text-sm text-right text-[#525252]">{r.totalLots?.toLocaleString()}</td>
                <td className="py-2 px-3 text-sm text-right text-[#171717] font-medium">{r.messagesReceived?.toLocaleString() || '—'}</td>
                <td className="py-2 px-3 text-sm text-right text-[#171717] font-medium">{r.meetingsAttended}</td>
                <td className="py-2 px-3 text-sm text-right text-[#171717] font-medium">{r.callsTotal?.toLocaleString() || '—'}</td>
                <td className="py-2 px-3 text-sm text-right text-[#525252]">{r.talkTimeMinutes?.toLocaleString()}</td>
                <td className="py-2 px-3 text-sm text-right text-[#171717] font-mono">{r.totalHoursWeekly?.toFixed(1) ?? '—'}</td>
                <td className="py-2 px-3 text-sm text-right text-[#171717] font-mono">{r.trackedVsTeamAvgPct != null ? `${Math.round(r.trackedVsTeamAvgPct)}%` : '—'}</td>
                <td className="py-2 px-3"><WorkloadChip bandKey={r.workloadBandKey} pct={r.trackedVsTeamAvgPct} hoursPerWeek={r.totalHoursWeekly} showPct={false} /></td>
                <td className="py-2 px-3"><PulseCell pulse={r.pulse} /></td>
                <td className="py-2 px-3 text-sm text-right font-bold text-[#171717]">{r.workloadIndex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkloadAnalysis;

// ── Everperform Pulse cell ────────────────────────────────────────────────
// Chip colour tracks the Care Watchlist band so a "Critical" row here reads
// identically to a "Critical" row on the Dashboard's Care Watchlist card.
const PULSE_STYLE = {
  critical: 'bg-rose-100 text-rose-800 border-rose-300',
  watch:    'bg-amber-100 text-amber-900 border-amber-300',
  fine:     'bg-emerald-50 text-emerald-800 border-emerald-300',
  unknown:  'bg-[#FAFAFA] text-[#737373] border-[#E5E5E5]',
};
const PulseCell = ({ pulse }) => {
  if (!pulse || pulse.band === 'unknown' || pulse.compositeScore == null) {
    return <span className="text-[#A3A3A3] text-xs">—</span>;
  }
  const style = PULSE_STYLE[pulse.band] || PULSE_STYLE.unknown;
  const tip = [
    `${pulse.label} · composite ${Math.round(pulse.compositeScore)}/100`,
    pulse.compositeVariance != null ? `${pulse.compositeVariance > 0 ? '+' : ''}${Math.round(pulse.compositeVariance)} vs prev month` : null,
    pulse.atRiskTotal > 0 ? `${pulse.atRiskTotal} at-risk factors` : null,
    ...(pulse.triggers || []),
  ].filter(Boolean).join('\n');
  const arrow = pulse.compositeVariance == null || Math.abs(pulse.compositeVariance) < 0.5
    ? '·' : pulse.compositeVariance > 0 ? '↑' : '↓';
  return (
    <span
      data-testid={`pulse-chip-${pulse.band}`}
      title={tip}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${style}`}
    >
      {Math.round(pulse.compositeScore)}
      <span className="text-[10px] opacity-70">{arrow}</span>
      {pulse.band !== 'fine' && <span className="text-[9px] uppercase tracking-wider opacity-80">{pulse.band}</span>}
    </span>
  );
};
